import { useEffect, useState } from "react";
import { usePaginationSync } from "../../../../hooks/usePaginationSync";
import { useGetAssetGroups } from "../../../../helpers/queries/scans/useGetInventory";
import {
  cn,
  convertTargetToArray,
  formatFullDate,
  formatTime,
} from "../../../../lib/utils";
import { useGetUserRole } from "../../../../hooks/useGetUserRole";
import { Button } from "../../../../components/ui/button";
import { FiCompass, FiEdit, FiTrash2 } from "react-icons/fi";
import empty from "../../../../assets/images/empty_assets.png";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteTarget } from "../../../../helpers/api";
import { toast } from "react-toastify";
import DeleteModal from "../../../../components/modals/DeleteModal";
import EditTargetModal from "../../Targets/modals/EditTargetModal";
import RunScanModal from "../../Targets/modals/RunScanModal";
import { Link } from "react-router-dom";
import { SquareArrowOutUpRight } from "lucide-react";
import { Spinner } from "../../../../components/Spinner";
import { PaginationComponent } from "../../../../components/Pagination";
import DataTable from "../../../../components/DataTable";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";

const GroupedAsset = () => {
  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const { isLoading, isSuccess, isError, data } =
    useGetAssetGroups(currentPage);
  const { role } = useGetUserRole();
  useEffect(() => {
  if (data?.pagination?.last_page) {
    setLastPage(data.pagination.last_page);
  }
}, [data?.pagination?.last_page]);

  const [targetData, setTargetData] = useState({});
  const [openEdit, setOpenEdit] = useState(false);
  const [openScan, setOpenScan] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(null);
  const handleDeleteModal = () => setOpenDeleteModal(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const queryClient = useQueryClient();
  const { mutate: deleteMutation, isPending } = useMutation({
    mutationFn: (id) => deleteTarget(id),
    onSuccess: () => {
      toast("Asset deleted successfully");
      handleDeleteModal();
      queryClient.invalidateQueries({ queryKey: ["targets"] });
      queryClient.invalidateQueries({ queryKey: ["asset_groups"] });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete asset");
    },
  });

  const handleDelete = (id) => deleteMutation(id);

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      width: "40%",
      cell: ({ row }) => <div className="truncate">{row.original.name}</div>,
    },
    {
      accessorKey: "target",
      header: <span className="">Count</span>,
      width: "10%",
      cell: ({ row }) => {
        const parts = row.original.target.split(",").map((p) => p.trim());
        return <span className="block text-center">{parts.length}</span>;
      },
    },
    {
      accessorKey: "created_at",
      header: "Created On",
      width: "20%",
      cell: ({ row }) => (
        <div>
          <span className="text-neutral800">
            {formatFullDate(row.original.created_at)},
          </span>{" "}
          <span className="text-neutral600">
            {formatTime(row.original.created_at)}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "last_scanned",
      header: "Last Scanned",
      width: "20%",
      cell: ({ row }) =>
        row.original.last_scanned ? (
          <div>
            <span className="text-neutral800">
              {formatFullDate(row.original.last_scanned)},
            </span>{" "}
            <span className="text-neutral600">
              {formatTime(row.original.last_scanned)}
            </span>
          </div>
        ) : (
          <div>-</div>
        ),
    },

    {
      id: "action",
      header: () => (
        <span
          className={cn(
            role && role !== "Viewer" ? "block text-center" : "hidden"
          )}
        >
          Action
        </span>
      ),
      width: role && role !== "Viewer" ? "20%" : "10%",
      cell: ({ row }) => {
        const item = row.original;
        const targetList = convertTargetToArray(item?.target);

        return (
          <TooltipProvider>
            <div
              className={cn(
                role && role === "Viewer" ? "justify-start" : "justify-end",
                "flex items-center space-x-2"
              )}
            >
              {targetList?.length > 1 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`/dashboard/assets/${item?.id}`}
                      state={{ targetData: item, inventory: true }}
                    >
                      <Button
                        className="w-fit bg-blue-100 hover:bg-blue-200 text-neutral1000"
                        variant="ghost"
                      >
                        <SquareArrowOutUpRight size={16} />
                      </Button>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>View more</TooltipContent>
                </Tooltip>
              )}

              {role && role !== "Viewer" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-fit bg-neutral200 hover:bg-neutral300 text-neutral1000"
                        variant="ghost"
                        onClick={() => {
                          setTargetData(item);
                          setOpenScan(true);
                        }}
                      >
                        <FiCompass size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Scan target</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-fit bg-neutral200 hover:bg-neutral300 text-neutral1000"
                        variant="ghost"
                        onClick={() => {
                          setTargetData(item);
                          setOpenEdit(true);
                        }}
                      >
                        <FiEdit size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit target</TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                        variant="ghost"
                        onClick={() => {
                          setSelectedItem(item);
                          setOpenDeleteModal(true);
                        }}
                      >
                        <FiTrash2 size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Delete target</TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        );
      },
    },
  ];

  return (
    <div>
      {isLoading && !isSuccess && (
        <div className="h-[55vh] w-full flex items-center justify-center">
          <Spinner />
        </div>
      )}

      {isError && !isLoading && (
        <div className="h-[45vh] w-full flex justify-center flex-col items-center">
          <p className="font-semibold text-xl text-center">
            There was an error getting the data
          </p>
          <p className="text-center">{data?.message ?? ""}</p>
        </div>
      )}

      {!isLoading && isSuccess && data?.data?.length > 0 && (
        <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
          <DataTable columns={columns} data={data?.data} />

          <PaginationComponent
            lastPage={data?.pagination?.last_page}
            currentPage={currentPage}
            handlePageChange={updatePage}
          />
        </div>
      )}

      {data?.data?.length === 0 && !isLoading && isSuccess && (
        <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
          <img src={empty} alt="empty" />
          <p className=" font-semibold my-2 text-darkGrey">
            No groups have been created yet
          </p>
          <p className="text-neutral600 text-sm max-w-[360px] mb-4">
            Start by adding your first asset. This can be domains or IP
            addresses you'd like to monitor for vulnerabilities.
          </p>
          {/* {role && role !== "Viewer" && data?.data?.data?.length === 0 && (
            <AddTargetModal />
          )} */}
        </div>
      )}

      <EditTargetModal
        data={targetData}
        openEdit={openEdit}
        setOpenEdit={setOpenEdit}
        assetList={true}
      />

      <RunScanModal
        data={targetData}
        openScan={openScan}
        setOpenScan={setOpenScan}
      />

      <DeleteModal
        onOpenChange={setOpenDeleteModal}
        open={openDeleteModal}
        data={selectedItem?.name}
        id={selectedItem?.id}
        title="Delete asset"
        handleDelete={handleDelete}
        loading={isPending}
      />
    </div>
  );
};

export default GroupedAsset;
